import axios from 'axios';
import { buildBiosdkNistRouteUrl } from './biosdkRoutes';
import { bdbToUint8Array, isBchqTemplate } from './bdbUtils';
import {
  buildFaceExtractInnerRequest,
  buildFaceMatchInnerRequest,
  buildFaceProcessedSegment,
  buildFaceRawSegment,
  fileToRawBdbBytes,
  toSignedByteJson,
  wrapEncodedRequest,
  wrapMatchRequest,
} from './nistRequestBuilder';
import {
  NIST_FACE_MODE_FLAG,
  NIST_FACE_SCENARIO_TABLE6_MULTI_FACE,
  faceScenarioToModeFlag,
} from './nistConstants';
import { resolveFaceGalleryBdb, isPlainImageFile } from './nistGalleryFile';
import { MODALITY_FACE, pickBestGalleryMatch, rankGalleryDecisions } from './nistMatchRank';
import { parseExtractRecordMeta, parseExtractSegments, parseMatchDecisions } from './nistResponseParser';

export { toSignedByteJson, pickBestGalleryMatch, rankGalleryDecisions };

export async function extractNistFaceTemplate(biosdkBaseUrl, file, scenario = 'single') {
  const { templates } = await extractNistFaceTemplates(biosdkBaseUrl, [file], scenario);
  return templates[0]?.bdbBytes;
}

export async function extractNistFaceTemplates(biosdkBaseUrl, files, scenario = 'single') {
  if (!files?.length) throw new Error('No images provided for NIST face extract.');

  const segments = [];
  for (const file of files) {
    const bytes = await fileToRawBdbBytes(file);
    segments.push(buildFaceRawSegment(bytes, 'ENROLL'));
  }

  const flags = {
    [NIST_FACE_MODE_FLAG]: faceScenarioToModeFlag(scenario),
  };

  const inner = buildFaceExtractInnerRequest({ segments, flags });
  const body = await wrapEncodedRequest(inner);
  const url = buildBiosdkNistRouteUrl(biosdkBaseUrl, 'extract-processed-template');
  const extractResponse = await axios.post(url, body, {
    headers: { 'Content-Type': 'application/json' },
  });

  const parsed = parseExtractSegments(extractResponse.data, { requireBchq: true });
  if (!parsed.length) throw new Error('No BCHQ template in processed extract response');
  const meta = parseExtractRecordMeta(extractResponse.data);
  return { templates: parsed, meta, scenario };
}

export async function matchNistFaceTemplates(biosdkBaseUrl, probeBdbBytes, galleryBdbBytes) {
  const galleryList = Array.isArray(galleryBdbBytes) ? galleryBdbBytes : [galleryBdbBytes];
  const probeSegment = buildFaceProcessedSegment(probeBdbBytes, 'VERIFY');
  const gallerySegments = galleryList.map((bdb) => buildFaceProcessedSegment(bdb, 'VERIFY'));
  const inner = buildFaceMatchInnerRequest({
    probeSegment,
    gallerySegments,
    probeLevel: 'PROCESSED',
    galleryLevel: 'PROCESSED',
  });
  const finalBody = await wrapMatchRequest(inner);
  const url = buildBiosdkNistRouteUrl(biosdkBaseUrl, 'match');
  return axios.post(url, finalBody, { headers: { 'Content-Type': 'application/json' } });
}

async function resolveProbeBdb(biosdkBaseUrl, probeFile, scenario) {
  if (isPlainImageFile(probeFile)) {
    const extracted = await extractNistFaceTemplates(biosdkBaseUrl, [probeFile], scenario);
    const probeBdb = extracted.templates[0]?.bdbBytes;
    if (!probeBdb || !isBchqTemplate(probeBdb)) {
      throw new Error('Probe is not a valid BCHQ template after extract.');
    }
    return probeBdb;
  }
  const probeBdb = bdbToUint8Array(await probeFile.arrayBuffer());
  if (!isBchqTemplate(probeBdb)) {
    throw new Error('Probe is not a valid BCHQ template.');
  }
  return probeBdb;
}

export async function matchNistFaceWithGalleryFiles(
  biosdkBaseUrl,
  probeFile,
  galleryFiles,
  scenario = 'single'
) {
  const probeBdb = await resolveProbeBdb(biosdkBaseUrl, probeFile, scenario);
  const galleryBdbs = [];
  for (const gf of galleryFiles) {
    galleryBdbs.push(await resolveFaceGalleryBdb(biosdkBaseUrl, gf, scenario));
  }

  const matchResponse = await matchNistFaceTemplates(biosdkBaseUrl, probeBdb, galleryBdbs);
  const decisions = parseMatchDecisions(matchResponse.data, MODALITY_FACE);
  return { probeBdb, matchResponse, decisions };
}

/** Table 6: M probe templates (group photo) vs N gallery templates. */
export async function matchNistFaceMxNMatrix(
  biosdkBaseUrl,
  groupPhotoFile,
  galleryFiles,
  galleryScenario = 'single'
) {
  const { templates } = await extractNistFaceTemplates(
    biosdkBaseUrl,
    [groupPhotoFile],
    NIST_FACE_SCENARIO_TABLE6_MULTI_FACE
  );
  if (!templates.length) {
    throw new Error('Group photo produced no BCHQ probe templates.');
  }

  const galleryBdbs = [];
  for (const gf of galleryFiles) {
    galleryBdbs.push(await resolveFaceGalleryBdb(biosdkBaseUrl, gf, galleryScenario));
  }

  const matrix = [];
  for (let probeIndex = 0; probeIndex < templates.length; probeIndex++) {
    const probeBdb = templates[probeIndex].bdbBytes;
    const matchResponse = await matchNistFaceTemplates(biosdkBaseUrl, probeBdb, galleryBdbs);
    const decisions = rankGalleryDecisions(
      parseMatchDecisions(matchResponse.data, MODALITY_FACE),
      MODALITY_FACE
    );
    matrix.push({ probeIndex, decisions, matchResponse });
  }

  return { probeCount: templates.length, galleryCount: galleryBdbs.length, matrix };
}
